import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';

// Home and the essay index are the entry points, so they stay in the main bundle.
import Home from './pages/Home';
import DataEssays from './pages/DataEssays';
import ScrollToTop from './components/ScrollToTop';

// Styles
import './styles/main.css';

// Everything else is route-split. Each essay is its own page and loads its scenes'
// data on approach, so opening one never downloads another's datasets.
const Photography = lazy(() => import('./pages/Photography'));
const PortfolioDetail = lazy(() => import('./pages/PortfolioDetail'));
const VisualizingCorpora = lazy(() => import('./pages/VisualizingCorpora'));
const InternationalPolitics = lazy(() => import('./pages/InternationalPolitics'));
const ExploringTransformers = lazy(() => import('./pages/ExploringTransformers'));
const NetworkExplorer = lazy(() => import('./pages/NetworkExplorer'));
const JustForFun = lazy(() => import('./pages/JustForFun'));
const Spirograph = lazy(() => import('./pages/Spirograph'));
const CrystalMillPage = lazy(() => import('./pages/CrystalMillPage'));
const Contact = lazy(() => import('./pages/Contact'));
// Experiment. Nothing links to it — reachable only by typing the URL.
const GrammarEssay = lazy(() => import('./pages/GrammarEssay'));

const RouteFallback = () => <div className="route-fallback" />;

const App = () => {
  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/" exact component={Home} />
        <Route path="/about" exact render={() => <Redirect to="/" />} />
        <Route path="/photography" exact component={Photography} />
        <Route path="/contact" exact component={Contact} />
        <Route path="/portfolio/:id" exact component={PortfolioDetail} />
        <Route path="/data-essays" exact component={DataEssays} />
        {/* All three essays are scrolling pieces with a page each. The accordion
            they replaced is gone, along with the EssayDetail route that served it. */}
        <Route
          path="/data-essays/visualizing-corpora"
          exact
          component={VisualizingCorpora}
        />
        <Route
          path="/data-essays/international-politics-of-information"
          exact
          component={InternationalPolitics}
        />
        <Route
          path="/data-essays/exploring-transformers"
          exact
          component={ExploringTransformers}
        />
        {/* No data, no argument — a toy. Must precede the catch-all below. */}
        <Route path="/data-essays/just-for-fun" exact component={JustForFun} />
        <Route path="/data-essays/spirograph" exact component={Spirograph} />
        <Route path="/data-essays/mandala-mill" exact component={CrystalMillPage} />
        {/* Full-page interactive networks, linked from the essays. Must precede
            the catch-all below. */}
        <Route path="/data-essays/explore/:id" exact component={NetworkExplorer} />
        {/* Anything else under /data-essays goes back to the index, which is what
            EssayDetail used to do for an unknown slug. Also catches the temporary
            /exploring-transformers/scrolly preview URL. */}
        <Route
          path="/data-essays/:slug"
          render={() => <Redirect to="/data-essays" />}
        />
        {/* Standalone sketch, deliberately outside /data-essays so it does not read
            as one of the published essays. */}
        <Route path="/grammar-essay" exact component={GrammarEssay} />
        <Route path="/visualizations" exact render={() => <Redirect to="/data-essays" />} />
        <Route path="/scrolly-test" exact render={() => <Redirect to="/data-essays/visualizing-corpora" />} />

        {/* GitHub redirects */}
        <Route path="/github-simpsons" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/SimpsonsGPT';
          return null;
        }} />
        <Route path="/github-economist" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/EconomistGPT';
          return null;
        }} />
        <Route path="/github-keyword" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/t5_keyword_to_text';
          return null;
        }} />
        <Route path="/github-t5-dzongkha" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/T5-for-dzongkha-romanization';
          return null;
        }} />
        <Route path="/github-chileno" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/ChilenoGPT';
          return null;
        }} />
        <Route path="/github-chatgpt" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/longform_edtech_BERT';
          return null;
        }} />
        <Route path="/github-bert-ed" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/BERT_edtech_tools';
          return null;
        }} />
        <Route path="/github-bert-document" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/nlp_social_science/blob/main/document_similarity_with_BERT.ipynb';
          return null;
        }} />
        <Route path="/github-bert-heatmap" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/data_prep_for_visualizations/blob/main/notebooks/BERT_vector_space.ipynb';
          return null;
        }} />
        <Route path="/github-literary" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/NLP_literary_analysis';
          return null;
        }} />
        <Route path="/github-orthographic" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/data_prep_for_visualizations/blob/main/notebooks/character_counts.ipynb';
          return null;
        }} />
        <Route path="/github-social-science" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/nlp_social_science';
          return null;
        }} />
        <Route path="/github-linguistic" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/linguistic_similarity_mapping';
          return null;
        }} />
        <Route path="/github-ritmo" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/deep_ritmo';
          return null;
        }} />
        <Route path="/github-french" component={() => {
          window.location.href = 'https://github.com/s-cafferty-nlp/french_translator_pytorch';
          return null;
        }} />
      </Switch>
      </Suspense>
    </Router>
  );
};

export default App;
